import { useState, type ReactNode } from 'react';

/** Pane width is a layout concern owned by the shared UI layer. */
export function PaneContent({ width, className, children }: { width?: number | undefined; className?: string; children: ReactNode }) {
  return <div className={className} style={width === undefined ? undefined : { width, maxWidth: 'calc(100% - 20rem)' }}>{children}</div>;
}

/** Shared bounded pane width; browser storage is optional and never carries mailbox identities. */
export function usePaneWidth(storageKey: string, defaultWidth: number, min = 240, max = 560) {
  const clamp = (value: number) => Math.max(min, Math.min(max, value));
  const [width, setWidth] = useState(() => {
    try {
      const stored = Number(localStorage.getItem(storageKey));
      return stored > 0 && Number.isFinite(stored) ? clamp(stored) : defaultWidth;
    } catch { return defaultWidth; }
  });
  const resize = (value: number) => {
    const next = clamp(value);
    setWidth(next);
    try { localStorage.setItem(storageKey, String(next)); } catch { /* Optional preference. */ }
  };
  return { width, resize, min, max, reset: () => resize(defaultWidth) };
}

/** Drag or use arrow keys; Home/End reach bounds and double-click restores the default. */
export function PaneResizeHandle({ pane, label }: { pane: ReturnType<typeof usePaneWidth>; label: string }) {
  return <div role="separator" aria-orientation="vertical" aria-label={label}
    aria-valuemin={pane.min} aria-valuemax={pane.max} aria-valuenow={pane.width} tabIndex={0}
    className="z-10 w-1 shrink-0 touch-none cursor-col-resize bg-border hover:bg-primary/40 focus-visible:bg-primary/40 focus-visible:outline-none"
    onDoubleClick={pane.reset}
    onKeyDown={event => {
      if (event.key === 'ArrowLeft') pane.resize(pane.width - 16);
      else if (event.key === 'ArrowRight') pane.resize(pane.width + 16);
      else if (event.key === 'Home') pane.resize(pane.min);
      else if (event.key === 'End') pane.resize(pane.max);
      else return;
      event.preventDefault();
    }}
    onPointerDown={event => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      event.currentTarget.dataset.startX = String(event.clientX);
      event.currentTarget.dataset.startWidth = String(pane.width);
    }}
    onPointerMove={event => {
      if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
      pane.resize(Number(event.currentTarget.dataset.startWidth) + event.clientX - Number(event.currentTarget.dataset.startX));
    }}
    onPointerUp={event => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    }} />;
}
